import { Button } from "@client/components/ui/button";
import type { TurnstileRef } from "@client/components/ui/turnstile";
import { captureClientException } from "@client/lib/sentry";
import { trpc, trpcClient } from "@client/lib/trpc-client";
import {
	AboutLlmsInfo,
	WhatsNextInfo,
} from "@client/routes/(protected)/-components/about-llms-info";
import { GenerationOptionsView } from "@client/routes/(protected)/-components/generation-options-view";
import { UrlDiscoveryInfo } from "@client/routes/(protected)/-components/url-discovery-info";
import { UrlEntryForm } from "@client/routes/(protected)/-components/url-entry-form";
import { NewSiteSearchSchema } from "@shared/entities/routes";
import {
	createFileRoute,
	isRedirect,
	redirect,
	useNavigate,
} from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/(protected)/sites/new")({
	beforeLoad: async ({ search }) => {
		try {
			// Get user's site count and plan limit
			const sitesQuery = await trpcClient.sites.list.query();
			// Get user's credit balance
			const creditsQuery = await trpcClient.credits.getBalance.query();
			// Get user's subscription to determine actual plan limits
			const subscriptionQuery = await trpcClient.user.getSubscription.query();

			// Calculate current limits using actual user plan
			const siteCount = sitesQuery.length || 0;
			const userPlan = subscriptionQuery?.plan ?? "free";

			// Use centralized plan limit calculation
			const { getPlanSiteLimit } = await import("@client/lib/plans");
			const planSiteLimit = getPlanSiteLimit(userPlan);

			const creditBalance = creditsQuery.balance?.balance || 0;
			const minCreditsNeeded = 10; // Minimum credits needed for smallest site

			// If URL is provided, check if it's an existing site (regeneration)
			const searchParams = search as { url?: string };
			let isExistingSite = false;

			if (searchParams.url) {
				// Check if this URL already exists in user's sites
				const normalizedUrl = searchParams.url.startsWith("http")
					? searchParams.url
					: `https://${searchParams.url}`;

				isExistingSite = sitesQuery.some(
					(site) =>
						site.url === normalizedUrl ||
						site.url === normalizedUrl.replace("https://www.", "https://") ||
						site.url === normalizedUrl.replace("https://", "https://www."),
				);
			}

			// Only check site limit for new sites (not regeneration)
			if (!isExistingSite && siteCount >= planSiteLimit) {
				toast.error(
					"You've reached your plan's site limit. Please upgrade to add more sites.",
				);
				throw redirect({ to: "/settings/billing" });
			}

			// Check if user has enough credits
			if (creditBalance < minCreditsNeeded) {
				toast.error(
					"You don't have enough credits to generate a new site. Please purchase more credits.",
				);
				throw redirect({ to: "/settings/billing" });
			}
		} catch (error) {
			// Re-throw redirects so TanStack Router can handle them
			if (isRedirect(error)) {
				throw error;
			}
			// If there's an error accessing the API, let the user continue
			// They'll be prevented from generating in the component
			console.error("Error checking limits:", error);
		}
	},
	component: NewSitePage,
	validateSearch: NewSiteSearchSchema,
});

function NewSitePage() {
	const navigate = useNavigate();
	const search = Route.useSearch();

	const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
	const turnstileRef = useRef<TurnstileRef>(null);

	// Get URL from search params or use empty string
	const initialUrl = search.url || "";
	const showOptions = search.showOptions || false;

	const createSiteMutation = trpc.sites.create.useMutation({
		onSuccess: () => {
			toast.success("Site added successfully");
		},
		onError: (error) => {
			toast.error(error.message || "Failed to add site");

			// Report to Sentry
			captureClientException(new Error(error.message), {
				tags: {
					operation: "createSite",
					component: "NewSitePage",
				},
				extra: {
					trpcError: error,
				},
			});
		},
	});

	const trpcUtils = trpc.useUtils();

	const generateMutation = trpc.generation.generate.useMutation({
		onSuccess: (data) => {
			console.log("Generation started:", data);
			toast.success("Generation started successfully!");

			// Invalidate credit balance - credits were just deducted by backend
			trpcUtils.credits.invalidate();
			// Invalidate active jobs and recent activities so the dashboard updates immediately
			trpcUtils.cleanupJobs.getActiveJobs.invalidate();
			trpcUtils.sites.getRecentActivities.invalidate();

			// Navigate to the generation status page
			if (data.jobId) {
				navigate({ to: "/generation/$jobId", params: { jobId: data.jobId } });
			}
		},
		onError: (error) => {
			console.error("Generation failed:", error);
			toast.error(`Failed to start generation: ${error.message}`);
		},
	});

	// No longer need polling logic here since we redirect immediately

	const handleCreateSiteOnly = (url: string) => {
		// Normalize URL if needed
		let normalizedUrl = url;
		if (
			!normalizedUrl.startsWith("http://") &&
			!normalizedUrl.startsWith("https://")
		) {
			normalizedUrl = `https://${normalizedUrl}`;
		}

		createSiteMutation.mutate(
			{ url: normalizedUrl },
			{
				onSuccess: (data) => {
					navigate({
						to: "/sites/$siteId",
						params: { siteId: data.id },
						search: { jobId: undefined },
					});
				},
			},
		);
	};

	const handleGenerate = (options: {
		maxUrls: number;
		showFullText: boolean;
		includeModificationDate: boolean;
		generationMethod: "simple" | "enhanced";
		useSitemap: boolean;
		// Discovery ID to reuse already-discovered URLs
		discoveryId?: string;
	}) => {
		if (!turnstileToken) {
			toast.error(
				"Please complete the verification challenge before generating",
			);
			return;
		}

		const url = search.url || "";
		const siteId = search.siteId; // Get site ID from search params

		// First normalize the URL if needed
		let normalizedUrl = url;
		if (
			!normalizedUrl.startsWith("http://") &&
			!normalizedUrl.startsWith("https://")
		) {
			normalizedUrl = `https://${normalizedUrl}`;
		}

		console.log(
			`Starting generation for URL: ${normalizedUrl}${siteId ? ` (site ID: ${siteId})` : ""}`,
		);

		// If we have a site ID, start generation directly
		// Otherwise, the backend will handle site creation (fallback)
		const generationOptions = {
			url: normalizedUrl,
			options: {
				maxUrls: options.maxUrls,
				showFullText: options.showFullText,
				includeModificationDate: options.includeModificationDate,
				generationMethod: options.generationMethod,
				useSitemap: options.useSitemap,
				turnstileToken,
				siteId, // Pass site ID if available
				discoveryId: options.discoveryId, // Pass discoveryId to reuse URLs
			},
		};

		console.log("Starting generation with options:", generationOptions);
		console.log(
			options.discoveryId
				? `Reusing discovered URLs from discovery ${options.discoveryId}`
				: "Will discover URLs during generation",
		);
		generateMutation.mutate(generationOptions);
	};

	const handleBackToUrlEntry = () => {
		navigate({
			to: "/sites/new",
			search: { url: undefined, showOptions: false },
		});
	};

	const handleTurnstileVerify = (token: string) => {
		setTurnstileToken(token);
	};

	const handleTurnstileError = () => {
		setTurnstileToken(null);
		toast.error("Verification failed. Please try again.");
	};

	const handleTurnstileExpire = () => {
		setTurnstileToken(null);
		toast.warning("Verification expired. Please complete it again.");
	};

	// Generation is handled on separate status page now
	const isGenerating = generateMutation.isPending;

	return (
		<div className="container mx-auto max-w-5xl px-4 py-8">
			<Button
				variant="ghost"
				size="sm"
				className="mb-4"
				onClick={() => {
					if (showOptions) {
						handleBackToUrlEntry();
					} else {
						navigate({ to: "/sites" });
					}
				}}
				disabled={isGenerating}
			>
				<ArrowLeft className="mr-2 h-4 w-4" />
				{showOptions ? "Back to URL entry" : "Back to sites"}
			</Button>

			<div className="mb-8">
				<h1 className="mb-2 font-bold text-3xl">
					{showOptions ? "Generate LLMs.txt File" : "Add New Site"}
				</h1>
				<p className="text-lg text-muted-foreground">
					{showOptions
						? "Configure your generation settings below"
						: "Start by entering your website URL"}
				</p>
			</div>

			{!showOptions ? (
				<div className="space-y-6">
					<UrlDiscoveryInfo />

					<div className="grid gap-6 md:grid-cols-2">
						<UrlEntryForm
							initialUrl={initialUrl}
							onCreateSiteOnly={handleCreateSiteOnly}
							isCreatingSite={createSiteMutation.isPending}
						/>

						<div className="flex flex-col space-y-4">
							<AboutLlmsInfo />
							<WhatsNextInfo />
						</div>
					</div>
				</div>
			) : (
				<GenerationOptionsView
					siteUrl={search.url || ""}
					initialUrl={initialUrl}
					siteId={search.siteId}
					onGenerate={handleGenerate}
					onCreateSiteOnly={handleCreateSiteOnly}
					isGenerating={isGenerating}
					isCreatingSite={createSiteMutation.isPending}
					turnstileToken={turnstileToken}
					turnstileRef={turnstileRef}
					onTurnstileVerify={handleTurnstileVerify}
					onTurnstileError={handleTurnstileError}
					onTurnstileExpire={handleTurnstileExpire}
				/>
			)}
		</div>
	);
}
